/**
 * The shortcuts drawer, and the only discovery surface for the keyboard.
 *
 * It renders straight off `lib/shortcuts.ts`. There is no list of rows in this
 * file and there must never be one: a second copy of the keys is how the
 * captions and the bindings came apart in the first place.
 *
 * EVERY ROW IS ALWAYS LISTED, including the ones that need something to be open
 * before they do anything. Those print their precondition instead of vanishing.
 * Hiding them would answer "why did nothing happen" with an empty space, and
 * somebody reading this panel to learn the keyboard is precisely somebody who
 * has not met the precondition yet.
 */
import { createContext, useContext, type ReactNode } from 'react'
import { KeyCombo } from './Keycap'
import {
  SHORTCUT_GROUPS,
  shortcutById,
  shortcutRequirement,
  shortcutsInGroup,
  type ShortcutGroup,
} from '../lib/shortcuts'
import { Drawer } from './Drawer'

const TITLE = 'Keyboard shortcuts'
const SUBTITLE = 'Shortcuts available across the console'

/**
 * The grouped rows on their own.
 *
 * Separate from the drawer because the drawer portals into the document and
 * this workspace has no DOM to portal into. Everything worth asserting about
 * this panel is in the markup below, so the markup is what a test renders.
 */
export function ShortcutsList({ className }: { className?: string }) {
  return (
    <div className={className}>
      {SHORTCUT_GROUPS.map((group) => (
        <ShortcutGroupBlock key={group} group={group} />
      ))}
    </div>
  )
}

function ShortcutGroupBlock({ group }: { group: ShortcutGroup }) {
  const rows = shortcutsInGroup(group)
  // A group with nothing in it is a heading over a gap. It can happen while a
  // group is being filled in, so it is skipped rather than drawn empty.
  if (rows.length === 0) return null

  /*
    THE REFERENCE'S LIST, NOT A STACK OF CARDS. Salesforge draws each group as
    an uppercase label over plain rows split by hairlines, at least 42px tall,
    with the caps at the right. Cards around every row made a forty-row panel
    read as forty separate things.
  */
  return (
    <section className="mb-6 last:mb-0">
      <h3 className="pb-1 text-xs font-semibold uppercase tracking-[0.3px] text-ink-subtle">{group}</h3>
      <ul className="divide-y divide-line">
        {rows.map((shortcut) => {
          const requires = shortcutRequirement(shortcut)
          return (
            <li key={shortcut.id} className="flex min-h-[2.625rem] items-start justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="text-base font-medium text-ink">{shortcut.title}</p>
                <p className="text-base text-ink-muted">{shortcut.description}</p>
                {/* Not truncated and not hidden. An ellipsis would turn a stated
                    precondition back into the mystery it exists to answer. */}
                {requires === null ? null : <p className="mt-0.5 text-xs text-ink-subtle">{requires}</p>}
              </div>
              <KeyCombo combo={shortcut.binding} className="mt-0.5" />
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export function ShortcutsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer open={open} onClose={onClose} width="sm" title={TITLE} description={SUBTITLE}>
      <ShortcutsList />
    </Drawer>
  )
}

/*
 * THE OPENER, HANDED DOWN RATHER THAN PASSED THROUGH.
 *
 * Two things open this panel: the key, which the shell binds, and the Shortcuts
 * link at the bottom of the sidebar. The state belongs in the shell, and the
 * link is several components below it with nothing else to do with shortcuts,
 * so threading a callback down would put a prop on every component in between
 * that only exists to be forwarded.
 */
const OpenShortcuts = createContext<(() => void) | null>(null)

export function ShortcutsOpener({ onOpen, children }: { onOpen: () => void; children: ReactNode }) {
  return <OpenShortcuts.Provider value={onOpen}>{children}</OpenShortcuts.Provider>
}

/**
 * Null outside the shell, rather than throwing, so a component that carries the
 * Shortcuts link can still be rendered on its own in a test.
 */
export function useOpenShortcuts(): (() => void) | null {
  return useContext(OpenShortcuts)
}

/**
 * The cap for the sidebar's Shortcuts link, read off the same row the key is
 * bound from. Written out by hand it would be the one caption in the app still
 * free to disagree with its key. One joined cap at the inline size, because it
 * is a hint beside a row rather than a line in a list.
 */
export function ShortcutsKeyCaps({ className }: { className?: string }) {
  return <KeyCombo joined size="inline" combo={shortcutById('app.shortcuts').binding} className={className} />
}
